import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { ProductEntryRequirement } from '@adornis/buchungsheld-db/entities/ProductEntryRequirement.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/entity-picker/client/x-entity-picker.js';
import '@adornis/forms/x-input.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/*
 * @element bh-prompt-admin-manage-product-entry-requirement
 */
@customElement('bh-prompt-admin-manage-product-entry-requirement')
export class BhPromptAdminManageProductEntryRequirement extends TrDialog<ProductEntryRequirement | null> {
  static override get element_name() {
    return 'bh-prompt-admin-manage-product-entry-requirement';
  }

  @property({ attribute: false }) mode: 'add' | 'edit' = 'add';
  @property({ attribute: false }) requirementClass!: string;
  @property({ attribute: false }) institutionID?: string;
  @property({ attribute: false }) entryRequirement = new RXController<null | ProductEntryRequirement>(this, null);

  @state() private _selectedRequirementKind = new RXController<typeof ProductEntryRequirement | null>(
    this,
    this.requirementKinds.at(0),
  );

  get requirementKinds() {
    const productEntryRequirementsPlugin = getPluginForScope(
      'registration::product-entry-requirements',
      this.institutionID ?? 'default',
    );
    return productEntryRequirementsPlugin.getRequirementKinds();
  }

  get actionText() {
    if (this.mode === 'add') return this._translation.translate('add');
    return (this._translation.translate('edit') as any).toLowerCase();
  }

  get actionButtonText() {
    if (this.mode === 'add') return this._translation.translate('add');
    return this._translation.translate('save');
  }

  override content() {
    if (this.entryRequirement.isLoading) return html` <tr-loading-state></tr-loading-state> `;
    if (!this.entryRequirement.value || !this.institutionID) return html` 404 - Not found `;
    this.entryRequirement.value.institutionID = this.institutionID;

    return html`
      <x-flex space="md">
        <x-h2> Anforderungen ${this.actionText} </x-h2>
        ${(() => {
          // only make requirement kind selection available when adding
          if (this.mode === 'edit') return nothing;
          return html`
            <tr-dropdown-selection
              .value=${this._selectedRequirementKind.value}
              .selectables=${this.requirementKinds}
              .renderString=${(requirement: typeof ProductEntryRequirement) =>
                this._translation.translate('productEntryRequirement', requirement._class as any)}
              @value-picked=${this.selectRequrementKind.bind(this)}
            ></tr-dropdown-selection>
          `;
        })()}
        ${this.entryRequirement.value.renderEditUI({ tr: this._translation })}
        <x-flex horizontal end>
          <x-button @click=${() => this.close(this.entryRequirement.value)}> ${this.actionButtonText} </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  selectRequrementKind(e: ValueEvent<typeof ProductEntryRequirement>) {
    const requirementKind = e.detail.value;
    this._selectedRequirementKind.next(requirementKind);

    // create new requirement
    const currentRequirement = this.entryRequirement.value;
    const requirement = new requirementKind({
      _id: currentRequirement?._id,
      institutionID: this.institutionID,
      productID: currentRequirement?.productID,
    });
    this.entryRequirement.next(requirement);
  }
}
